import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { convertCurrency, type Currency } from "@/lib/currency-conversion";

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatAmount: (amount: number, sourceCurrency?: Currency) => string;
  convertAmount: (amount: number, from: Currency, to: Currency) => number;
  currencySymbol: string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const currencySymbols: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  MAD: "MAD",
  GBP: "£"
};

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] = useState<Currency>("USD");

  useEffect(() => {
    const loadCurrency = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("currency_preference")
          .eq("id", user.id)
          .maybeSingle();
        
        if (data?.currency_preference) {
          setCurrencyState(data.currency_preference as Currency);
        }
      }
    };
    loadCurrency();
  }, []);

  const setCurrency = async (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from("profiles")
        .update({ currency_preference: newCurrency })
        .eq("id", user.id);
    }
  };

  const convertAmount = (amount: number, from: Currency, to: Currency): number => {
    return convertCurrency(amount, from, to);
  };

  const formatAmount = (amount: number, sourceCurrency?: Currency | string | null): string => {
    // Handle null/undefined source currency - default to USD since that's the base currency
    const source = (sourceCurrency as Currency) || "USD";
    
    // Convert from source currency to display currency
    const convertedAmount = convertCurrency(amount, source, currency);
    const symbol = currencySymbols[currency];
    
    if (currency === "MAD") {
      return `${convertedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${symbol}`;
    }
    return `${symbol}${convertedAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      formatAmount,
      convertAmount,
      currencySymbol: currencySymbols[currency]
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within CurrencyProvider");
  }
  return context;
};